/**
 * Wires up every full-screen mobile search overlay on the page
 * (snippets/je-search-full.liquid) to the visible search box it's paired
 * with, via a shared data attribute rather than one script per component
 * — unlike the CSS (duplicated per component to avoid cross-page style
 * leakage), this behavior is identical everywhere it's used, so one
 * generic script scanning for matching elements is the actual "avoid
 * repeating myself where it's safe to" case, not the exception to it.
 *
 * Trigger markup: the visible input gets
 * data-je-search-full-trigger="<dialog id>". Below 750px, tapping it opens
 * the matching <dialog> instead, moving focus to the dialog's own real
 * input. At 750px and up this script does nothing — those screens keep
 * the existing inline dropdown (assets/je-hero.js / je-drawer-search.js /
 * je-dest-sticky-bar-search.js).
 *
 * readonly alone isn't reliable here — on some mobile browsers a readonly
 * type="search" input still briefly shows the native keyboard before a
 * 'click'/'focus' handler gets a chance to blur it, so this also
 * preventDefaults on 'touchstart'/'mousedown' (which fire before the
 * browser hands focus to the input at all) as the primary block, with
 * readonly kept as a defensive backup.
 */
document.addEventListener('DOMContentLoaded', () => {
  if (!window.matchMedia('(max-width: 749px)').matches) return;

  document.querySelectorAll('[data-je-search-full-trigger]').forEach((trigger) => {
    const dialogId = trigger.getAttribute('data-je-search-full-trigger');
    const dialog = document.getElementById(dialogId);
    if (!dialog) return;

    const input = dialog.querySelector('[data-je-search-full-input]');
    const popularTitle = dialog.querySelector('[data-je-search-full-popular-title]');
    const popularGrid = dialog.querySelector('[data-je-search-full-popular-grid]');
    const allGrid = dialog.querySelector('[data-je-search-full-all-grid]');
    const emptyMsg = dialog.querySelector('[data-je-search-full-empty]');
    const closeBtn = dialog.querySelector('[data-je-search-full-close]');
    const allCards = allGrid ? Array.from(allGrid.querySelectorAll('[data-je-hero-dest]')) : [];

    trigger.setAttribute('readonly', 'readonly');

    function open(e) {
      if (e) e.preventDefault();
      if (dialog.open) return;
      trigger.blur();
      dialog.showModal();
      // Focus synchronously, not inside a setTimeout — iOS Safari only
      // opens the on-screen keyboard for a programmatic .focus() call if
      // it happens within the same synchronous task as the original user
      // gesture (this touchstart/click handler). Deferring it, even by a
      // few milliseconds, breaks that chain and the keyboard silently
      // never appears, even though the input is visibly focused.
      if (input) input.focus();
    }
    // touchstart/mousedown fire before the browser would hand the trigger
    // input focus — preventDefault here stops that focus (and the native
    // keyboard that comes with it) from ever happening, unlike 'click' or
    // 'focus' alone, which fire too late to reliably stop it on every
    // mobile browser.
    trigger.addEventListener('touchstart', open, { passive: false });
    trigger.addEventListener('mousedown', open);
    trigger.addEventListener('click', open);
    trigger.addEventListener('focus', open);

    if (closeBtn) {
      closeBtn.addEventListener('click', () => dialog.close());
    }

    function selectDestination(card) {
      const url = card.getAttribute('data-je-hero-dest-url');
      if (url) window.location.href = url;
    }

    dialog.addEventListener('click', (e) => {
      const card = e.target.closest('[data-je-hero-dest]');
      if (card) {
        e.preventDefault();
        selectDestination(card);
      }
    });

    function filterDestinations(query) {
      const q = query.trim().toLowerCase();

      if (!q) {
        if (popularTitle) popularTitle.hidden = false;
        if (popularGrid) popularGrid.hidden = false;
        if (allGrid) allGrid.hidden = true;
        if (emptyMsg) emptyMsg.hidden = true;
        return;
      }

      if (popularTitle) popularTitle.hidden = true;
      if (popularGrid) popularGrid.hidden = true;
      if (allGrid) allGrid.hidden = false;

      let anyVisible = false;
      allCards.forEach((card) => {
        const name = card.getAttribute('data-je-hero-dest-name').toLowerCase();
        const match = name.includes(q);
        card.hidden = !match;
        if (match) anyVisible = true;
      });
      if (emptyMsg) emptyMsg.hidden = anyVisible;
    }

    if (input) {
      input.addEventListener('input', () => filterDestinations(input.value));
    }

    const form = dialog.querySelector('form');
    if (form) {
      form.addEventListener('submit', (e) => {
        const query = input ? input.value.trim() : '';
        if (!query) {
          e.preventDefault();
          return;
        }
        const exact = allCards.find(
          (card) => card.getAttribute('data-je-hero-dest-name').toLowerCase() === query.toLowerCase()
        );
        if (exact) {
          e.preventDefault();
          selectDestination(exact);
        }
      });
    }
  });
});
